<script lang="ts" setup>
import { computed } from "vue";
import type { Config } from "datatables.net";
import DataView from "@/components/DataView.vue";
import { useEngineSeries, type EngineSeriesRequest } from "@/queries/engine-metrics";

/**
 * Who one host is busy with. The drill between a hot host and the work on it, and the only place the two
 * are on the same page.
 *
 * <p>What it does NOT show is CPU per org, because there is no such measurement and there cannot be: host
 * CPU comes from the runtime's own meter, which counts the process rather than the work inside it. What is
 * attributable is the engine's own timing — compute milliseconds a second is the share of a core this
 * tenant's transactions are taking, which is the same scale the hosts table reads CPU in.</p>
 */
const props = defineProps<{
  instanceId: string;
  orgName: (orgId: string) => string;
  minutesBack: number;
  stepSeconds: number;
}>();

const emit = defineEmits<{ (event: "select", orgId: string): void }>();

function onThisHost(extra: Partial<EngineSeriesRequest>): EngineSeriesRequest {
  return {
    series: "Transactions",
    aggregation: "Rate",
    splitBy: "Org",
    instanceId: props.instanceId,
    minutesBack: props.minutesBack,
    stepSeconds: props.stepSeconds,
    ...extra,
  };
}

const transactionsRequest = computed(() => onThisHost({}));
const computeRequest = computed(() =>
  onThisHost({ series: "TransactionCompute", aggregation: "Rate" })
);

const { lines: transactionLines, seriesLoading } = useEngineSeries(transactionsRequest);
const { lines: computeLines } = useEngineSeries(computeRequest);

/** What the series says now, which is the reading that answers "is this org on this host at the moment". */
const latest = (lines: { label: string; points: { value: number }[] }[], orgId: string) =>
  lines.find((line) => line.label === orgId)?.points.at(-1)?.value ?? 0;

const rows = computed(() => {
  const orgIds = new Set(
    [...transactionLines.value, ...computeLines.value].map((line) => line.label)
  );

  return [...orgIds]
    .filter(Boolean)
    .map((orgId) => ({
      orgId,
      orgName: props.orgName(orgId),
      transactions: latest(transactionLines.value, orgId),
      // Milliseconds of engine time per second, which is the share of a core — the hosts table's own scale.
      cores: latest(computeLines.value, orgId) / 1000,
    }))
    .sort((a, b) => b.cores - a.cores);
});

const columns = {
  columns: [
    { data: "orgName", title: "Org", render: "#orgCell" },
    { data: "transactions", title: "Transactions/s", render: (value: number) => value.toFixed(2) },
    { data: "cores", title: "Cores used", render: (value: number) => value.toFixed(2) },
  ],
  layout: { topEnd: "", bottomEnd: "", bottomStart: "" },
} as Config;
</script>

<template>
  <div>
    <h6 class="mb-1">Work on this host</h6>
    <p class="text-muted small mb-3">
      Ranked by the engine's own timing, which is what can be attributed to a tenant — a host's CPU is
      measured by the process and carries no org. Pick one to narrow the whole page to it.
    </p>

    <DataView :data="rows" :view-mode="'table'" :options="columns">
      <template #orgCell="slotProps">
        <a href="#" @click.prevent="emit('select', slotProps.rowData.orgId)">
          {{ slotProps.rowData.orgName }}
        </a>
      </template>

      <template #empty-state>
        <div class="text-muted small py-4 text-center">
          {{ seriesLoading ? "Reading…" : "This host ran nothing for anyone in this window." }}
        </div>
      </template>
    </DataView>
  </div>
</template>
